import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Option } from '@interfaces/common/option';
import { PartnerUserResponse } from '@interfaces/partners/responses/partner-user.response';
import { TranslateService } from '@ngx-translate/core';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { PartnersHttpService } from '@services/http/partners/partners-http.service';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { DialogComponent } from '@src/app/shared/components/dialog/dialog.component';
import { SharedModule } from '@src/app/shared/shared.module';

/**
 * Cadastro e edição de um usuário da equipe do parceiro, feitos pelo master.
 *
 * **Uma modal em dois modos**, e não duas telas: os campos são exatamente os mesmos quatro.
 * O que muda é o verbo do endpoint e o e-mail poder ou não ser editado.
 *
 * **Modal e não página inteira** — decisão de 17/09/2026. A tela interna de Gerenciamento ›
 * Usuários é página porque tem ~12 campos, a árvore de grupos de permissão e o painel de
 * escopo de empresas. Aqui são quatro campos: uma página seria um card com quatro inputs,
 * mais uma rota, um breadcrumb e um botão Cancelar para voltar — e o master perderia a lista
 * de vista no meio de uma tarefa que dura dez segundos.
 *
 * O que **não** aparece aqui, porque o servidor decide: tipo de usuário (sempre PARTNER),
 * parceiro (o do master logado), tenant, os grupos de permissão em si e a localização,
 * herdada do master.
 *
 * O **perfil** (comum ou master) passou a ser escolhido aqui em 18/09/2026, a pedido do
 * Breno — antes todo usuário nascia comum. É a única decisão de permissão que a modal toma,
 * e ela não escolhe grupo: manda um booleano que o backend traduz.
 */
@Component({
  selector: 'app-modal-partner-user',
  templateUrl: './modal-partner-user.component.html',
  imports: [CommonModule, ReactiveFormsModule, SharedModule],
})
export class ModalPartnerUserComponent extends VaultBaseComponent {
  @ViewChild('dialog') dialogRef: DialogComponent | null = null;

  /** Avisa a tela para recarregar a lista depois de um cadastro ou edição bem-sucedidos. */
  @Output() salvou = new EventEmitter<void>();

  /** Usuário em edição, ou `null` no modo cadastro. */
  usuarioEmEdicao: PartnerUserResponse | null = null;

  /**
   * Gravando. Nome próprio em vez de reusar o `isLoading` de `VaultBaseComponent`: aquele é
   * o estado do spinner **global**, alimentado pelo NgRx, e redeclará-lo aqui é erro de
   * compilação (`TS4114`) — sobrescrevê-lo acenderia o overlay da aplicação inteira por
   * causa de um POST de modal.
   */
  gravando = false;

  /**
   * O telefone nasce com `'55'`, como na tela de Gerenciamento › Usuários.
   *
   * A máscara é `+99 (99) 99999-9999`, ou seja, ela **inclui o DDI**. Começando vazio, quem
   * digitasse `11977776666` veria `+11 (97) 77766-66__` — a máscara consome os dois
   * primeiros dígitos como país. O `'55'` inicial põe o cursor no lugar certo, e
   * {@link PREFIXO_TELEFONE} desfaz isso no envio quando o campo não foi tocado.
   */
  private static readonly PREFIXO_TELEFONE = '55';

  /** Valor do seletor de perfil que significa "master". */
  private static readonly PERFIL_MASTER = 'master';

  /**
   * Perfis oferecidos no cadastro.
   *
   * "Comum" vem primeiro e é o default: é o perfil conservador, e é o que a maior parte da
   * equipe deve ser. Deixar "Master" primeiro convidaria ao clique distraído numa escolha
   * que o parceiro não consegue desfazer.
   */
  readonly opcoesDePerfil: Option[] = [
    { label: 'Comum', value: 'comum' },
    { label: 'Master', value: ModalPartnerUserComponent.PERFIL_MASTER },
  ];

  form: FormGroup = this.formBuilder.group({
    firstName: ['', [Validators.required]],
    surName: ['', [Validators.required]],
    email: ['', [Validators.required, Validators.email]],
    phoneNumber: [ModalPartnerUserComponent.PREFIXO_TELEFONE],
    perfil: ['comum', [Validators.required]],
  });

  constructor(
    translateService: TranslateService,
    private formBuilder: FormBuilder,
    private partnersService: PartnersHttpService,
    private toast: ToastHelperService,
    private cdr: ChangeDetectorRef
  ) {
    super(translateService);
  }

  get estaEditando(): boolean {
    return this.usuarioEmEdicao !== null;
  }

  /** Diz se o perfil escolhido no cadastro é master — usado para mostrar o aviso. */
  get perfilMasterSelecionado(): boolean {
    return this.form.get('perfil')?.value === ModalPartnerUserComponent.PERFIL_MASTER;
  }

  get titulo(): string {
    return this.estaEditando ? 'Editar usuário' : 'Cadastrar usuário';
  }

  /**
   * Diz se o e-mail está travado para edição.
   *
   * Trava depois de o usuário criar a senha: o hash do token de criação de senha é montado
   * sobre o endereço, e trocá-lo invalidaria links em trânsito sem avisar ninguém. O backend
   * recusa de todo modo — travar aqui evita que o master digite, salve e só então descubra.
   */
  get emailTravado(): boolean {
    return this.estaEditando && !!this.usuarioEmEdicao?.hasPassword;
  }

  /** Abre a modal em modo cadastro, com o formulário limpo. */
  async abrirParaCriar(): Promise<void> {
    this.usuarioEmEdicao = null;
    this.form.reset({
      firstName: '',
      surName: '',
      email: '',
      phoneNumber: ModalPartnerUserComponent.PREFIXO_TELEFONE,
      perfil: 'comum',
    });
    this.form.get('email')?.enable();

    this.cdr.markForCheck();
    await this.abrir();
  }

  /** Abre a modal em modo edição, preenchida com o usuário. */
  async abrirParaEditar(usuario: PartnerUserResponse): Promise<void> {
    this.usuarioEmEdicao = usuario;
    this.form.reset({
      firstName: usuario.firstName ?? '',
      surName: usuario.surName ?? '',
      email: usuario.email ?? '',
      phoneNumber: usuario.phoneNumber || ModalPartnerUserComponent.PREFIXO_TELEFONE,
      // O perfil não é editável: promover é ação própria, pelo menu da linha, com a sua
      // confirmação. Reaproveitar o seletor aqui esconderia uma mudança irreversível dentro
      // de um "Salvar" que as pessoas clicam sem ler.
      perfil: usuario.isMaster ? ModalPartnerUserComponent.PERFIL_MASTER : 'comum',
    });

    // `disable()` em vez de só `[disabled]` no template: desabilitado pelo FormControl sai
    // do `value` do formulário, então um e-mail travado nunca vai no payload por acidente.
    if (this.emailTravado) this.form.get('email')?.disable();
    else this.form.get('email')?.enable();

    this.cdr.markForCheck();
    await this.abrir();
  }

  isFormValid(): boolean {
    return this.form.valid;
  }

  /** Grava: cadastra ou edita, conforme o modo. */
  submitAction(): void {
    if (!this.isFormValid() || this.gravando) return;

    const bruto = this.form.getRawValue();

    // Só o DDI significa "não informou": a máscara inclui o país, e mandar "55" gravaria um
    // telefone que não existe. Mesmo tratamento da tela de Gerenciamento › Usuários.
    const telefone = (bruto.phoneNumber || '').trim();
    const telefoneInformado = telefone && telefone !== ModalPartnerUserComponent.PREFIXO_TELEFONE;

    const payload = {
      firstName: (bruto.firstName || '').trim(),
      surName: (bruto.surName || '').trim(),
      // No modo edição com e-mail travado o controle está desabilitado e não vem no
      // `value` — por isso o fallback para o e-mail atual, que o backend compara e aceita
      // como "não mudou".
      email: (bruto.email || this.usuarioEmEdicao?.email || '').trim(),
      phoneNumber: telefoneInformado ? telefone : undefined,
    };

    this.gravando = true;

    // `asMaster` só entra no POST: a edição não muda perfil, e mandar o campo num PUT que o
    // backend ignora criaria a impressão de que muda.
    const requisicao = this.estaEditando
      ? this.partnersService.updateMyPartnerUser(this.usuarioEmEdicao!.id, payload)
      : this.partnersService.createMyPartnerUser({
          ...payload,
          asMaster: bruto.perfil === ModalPartnerUserComponent.PERFIL_MASTER,
        });

    // `markForCheck` em TODO ponto que mexe em `gravando`: esta modal vive dentro de
    // `PartnerUsersComponent`, que é `OnPush`. O retorno do HTTP dispara a detecção a partir
    // da raiz, ela chega no pai marcado como limpo e **pula a subárvore inteira** — inclusive
    // esta modal. O sintoma medido em 18/09/2026: depois de um erro, `gravando` voltava a
    // `false` no componente e o botão continuava "Salvando...", desabilitado para sempre.
    // O caminho de sucesso escapava por sorte, porque o pai chamava `carregar()` (que faz o
    // seu próprio `markForCheck`) logo em seguida.
    this.cdr.markForCheck();

    requisicao.subscribe({
      next: () => {
        this.gravando = false;
        this.cdr.markForCheck();
        this.toast.showToastSuccess(
          this.estaEditando ? 'Usuário atualizado' : 'Usuário cadastrado',
          this.estaEditando ? '' : 'Ele recebeu por e-mail um link para criar a senha de acesso.'
        );
        this.dialogRef?.close();
        this.salvou.emit();
      },
      error: error => {
        this.gravando = false;
        this.cdr.markForCheck();
        // A mensagem do backend é a que diz o que fazer ("e-mail já tem acesso", "o e-mail
        // não pode mais ser alterado"). Texto genérico só para falha técnica.
        this.toast.showToastError(
          this.estaEditando ? 'Erro ao salvar o usuário' : 'Erro ao cadastrar o usuário',
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }

  /**
   * Abre o diálogo com o rodapé próprio.
   *
   * Os argumentos seguem o molde das outras modais do projeto: sem mensagem nem cabeçalho
   * do `ConfirmDialog`, porque o conteúdo e as ações vêm do template.
   */
  private async abrir(): Promise<void> {
    this.dialogRef?.open('', '', '', 'Cancelar', '', 'partner-user', false, true);
  }
}
